import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'
import { Router } from '@angular/router'

import { NotificationMessage } from '../../notification-message.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { TeamType } from '@shared/models/team.model'
import dayjs from 'dayjs'

@Component({
  selector: 'lin-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NotificationComponent {
  manuallyRead = false
  link: null | string

  private user = this.authService.getCurrentUser()

  private _notification: NotificationMessage

  @Input()
  set notification(notification: NotificationMessage) {
    this._notification = notification
    this.setLink()
  }

  get notification(): NotificationMessage {
    return this._notification
  }

  @Input() isRead = false

  @Output() markRead = new EventEmitter<string>()

  @HostBinding('class.is-read')
  get hasReadClass() {
    return this.manuallyRead || this.isRead
  }

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  markAsRead(): void {
    if (this.manuallyRead || this.isRead) return

    this.manuallyRead = true
    this.markRead.emit(this.notification.id)
  }

  handleLink(): void {
    this.markAsRead()
    // Navigate inside the current tab (John: clicking a notification used to `window.open(_, '_blank')`,
    // spawning a fresh Chrome tab each time). A client sitting in the v2 shell is routed to the v2 events
    // log so the click keeps them in v2 instead of dropping into the v1 UI.
    const target = this.resolveTarget()
    target && void this.router.navigateByUrl(target)
  }

  // Send a v2-shell client to the v2 events-log detail; everyone else (admin/reseller, or a client still
  // on v1) keeps the v1 deep-link. Same event id + query params, so the reused table behaves identically.
  private resolveTarget(): string | null {
    if (!this.link) return null
    if (this.user.isClient() && this.router.url.includes('/v2/')) {
      return this.link.replace(/^\/?client\/alerts\/events-log-details\/events-log\//, '/v2/client/events/log/')
    }
    // navigateByUrl treats its argument as an absolute URL, so guarantee a leading slash (the v1 link is
    // built without one for the old window.open() call).
    return this.link.startsWith('/') ? this.link : `/${this.link}`
  }

  private setLink(): void {
    const event = this.notification?.event
    const entityTeam = event?.entityTeam

    // v3 WebAlert has no numeric eventId (Q12) and entityTeam may lack `type` — hide the link instead
    // of building a broken /events-log/null URL (also guards a missing/malformed event object).
    if (!event || !entityTeam || event.eventId == null) {
      this.link = null
      return
    }

    const rootPath: string = this.user.isAdmin() ? 'admin' : 'client'
    const occurrenceTimeInMs = dayjs(this.notification.occurrenceTime).valueOf()

    this.link = `${rootPath}/alerts/events-log-details/events-log/${event.eventId}?eventTeam=${entityTeam.type}&occurrenceTime=${occurrenceTimeInMs}&eventLogId=${event.eventLogId}`
  }
}
